import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Firestore } from '@angular/fire/firestore';
import { Observable, Subject, Subscription, takeUntil } from "rxjs";
import { ApiService } from "src/app/core/backend/api.service";
import { ProfileStateDispatcher, USER_NAME_ACCEPTED_CHARACTERS, USER_NAME_MAX_LENGTH, USER_NAME_MIN_LENGTH } from "src/app/core/state/profile/profile.dispatcher";
import { ProfileData } from "src/app/core/state/profile/profile.state";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { DEFAULT_DISMISS_AFTER_TIME, DynamicContentService } from "../../services/dynamic-content/dynamic-content.service";
import { GameThemeSettingsDispatcher } from "src/app/core/state/gameThemeSettings/gameThemeSettings.dispatcher";
import { IAvatar } from "@soposopo/sopo-models";
import { GameSettingsService } from "src/app/core/firebase/gameSettings.service";
import { ProfileService } from 'src/app/core/firebase/profile.service';

@Component({
    selector: "app-profile-setup",
    templateUrl: "./profile-setup.component.html",
    styleUrls: ["./profile-setup.component.scss"],
    standalone: false,
})
export class ProfileSetupComponent implements OnInit, OnDestroy {
    viewIsOut = false;
    nameChangeForm = new FormGroup({
        username: new FormControl('')
    });
    minInputLength = USER_NAME_MIN_LENGTH;
    maxInputLength = USER_NAME_MAX_LENGTH;
    characterCount = 0;
    selectedAvatar: IAvatar;
    nameIsAlreadyTakenError = false;
    formIsLoaded = false;

    private profileSubscription: Subscription;
    public profile: ProfileData;
    public displayName = '';
    shortId = 0;
    myID: string;
    canEdit = false;
    nameUpdateInterval = 30; // in days
    ngUnsubscribe = new Subject<void>();
    avatarSubscription: Subscription;
    avatars: IAvatar[] = [];
    currentAvatarUrl: string;
    public validationMessages = {
        name: [
            {
                type: 'maxlength',
                message: `Name should not exceed ${USER_NAME_MAX_LENGTH} characters.`,
            },
            {
                type: 'pattern',
                message: 'Name should only contain English alphabetical characters.',
            },
            {
                type: 'minlength',
                message: `Name should be at least ${USER_NAME_MIN_LENGTH} characters long.`,
            }
        ],
    };


    constructor(
        private profileStateDispatcher: ProfileStateDispatcher,
        public firestore: Firestore,
        private ref: ChangeDetectorRef,
        private apiService: ApiService,
        private sopoPopupService: SopoPopupService,
        private gameThemeSettingsDispatcher: GameThemeSettingsDispatcher,
        private gameSettingsService: GameSettingsService,
        private dynamicContentService: DynamicContentService,
        private profileService: ProfileService,
    ) { }

    ngOnInit() {
        this.profileSubscription = this.profileStateDispatcher
            .getProfileState()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((profileState) => {
                if (!profileState || !profileState.profileData) return;
                this.profile = profileState.profileData;
                this.displayName = this.profile ? this.profile.displayName : '';
                if (this.formIsLoaded && this.displayName != this.nameChangeForm.value.username && !this.nameIsAlreadyTakenError) {
                    this.nameChangeForm.setValue({
                        username: this.displayName
                    });
                    this.characterCount = this.displayName.length;
                }
                this.myID = this.profile ? this.profile.uid : '';
                this.currentAvatarUrl = this.profile.photoURL as string
                if (this.profile && this.profile.lastNameChange != null) {
                    const currentTime = new Date().getTime();
                    const lastNameChange =
                        this.profile.lastNameChange.seconds * 1000;
                    const timeDiff = currentTime - lastNameChange;
                    const timeDiffInHr = timeDiff / (1000 * 3600);
                    const nameUpdateIntervalInHr = this.nameUpdateInterval * 24;
                    if (timeDiffInHr >= nameUpdateIntervalInHr) {
                        this.canEdit = true;
                    } else {
                        this.canEdit = false;
                    }
                } else {
                    this.canEdit = true;
                }
                this.shortId = profileState.profileData.shortID;
                this.ref.markForCheck();

                this.createForm();
            });
        this.avatarSubscription = this.gameThemeSettingsDispatcher.getMyAvatarsState().subscribe((avatars) => {
            this.avatars = avatars;
            this.selectedAvatar = this.avatars[0];
            this.ref.markForCheck();
        });
    }

    closeModal() {
        this.viewIsOut = true;
        setTimeout(() => {
            this.dynamicContentService.closeProfileSetup();
        }, 200)
    }

    handleInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        this.characterCount = input.value.length;
    }

    handleErrorMessageHide = () => {
        if (this.nameIsAlreadyTakenError) {
            document.getElementById("usernameInput").focus();
            this.nameIsAlreadyTakenError = false;
        }
    }

    createForm() {
        if (this.formIsLoaded) return;
        this.nameChangeForm = new FormGroup({
            username: new FormControl(null, [Validators.required, Validators.minLength(USER_NAME_MIN_LENGTH), Validators.maxLength(USER_NAME_MAX_LENGTH), Validators.pattern(USER_NAME_ACCEPTED_CHARACTERS)]),
        });
        this.nameChangeForm.controls.username.setValue(this.displayName);
        this.nameChangeForm.controls.username.markAsTouched();
        this.nameChangeForm.controls.username.markAsDirty();
        this.nameChangeForm.controls.username.updateValueAndValidity();
        this.characterCount = this.displayName.length;
        this.formIsLoaded = true;
    }

    handleAvatarSelect(avatar: IAvatar) {
        this.selectedAvatar = avatar;
    }

    async handleProfileSetup() {
        if (this.nameChangeForm.invalid) return;

        if (this.nameChangeForm.value.username && this.selectedAvatar) {
            try {
                const nameUpdateResponse = await this.apiService.updateName({ displayName: this.nameChangeForm.value.username.trim(), userID: this.myID })
                this.profileStateDispatcher.setLastNameChange();
                this.viewIsOut = true;
                setTimeout(() => {
                    this.sopoPopupService.clearViewContainer('app');
                    this.sopoPopupService.showAlert('app', {
                        header: nameUpdateResponse.header,
                        message: nameUpdateResponse.message,
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                        zIndex: 30
                    })
                }, 200)

                if (this.selectedAvatar.url != this.currentAvatarUrl) {
                    this.gameSettingsService.changeSetting(this.selectedAvatar, 'avatars');
                }

                await this.profileService.setProfileSetupToDone(this.myID);
                this.closeModal();
            } catch(error) {
                if ((error.error.message as string).includes("username is already taken")) {
                    this.nameIsAlreadyTakenError = true;
                    this.nameChangeForm.controls.username.setValue("");
                    this.characterCount = 0;
                    return;
                }
                const header = error.error.header ? error.error.header : 'Update Name'
                const message = error.error.message ? error.error.message : 'Update name failed.'
                this.sopoPopupService.clearViewContainer('app');
                this.sopoPopupService.showAlert('app', {
                    header: header,
                    message: message,
                    dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    zIndex: 50
                })
            }
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.profileSubscription) {
            this.profileSubscription.unsubscribe();
        }
        if (this.avatarSubscription) {
            this.avatarSubscription.unsubscribe();
        }
    }
}
